// Builds the dot-matrix land layer: Fibonacci-sphere points kept only where an
// equirectangular rasterization of the TopoJSON land polygons is filled.

import { feature } from "topojson-client";
import type { Topology, GeometryCollection } from "topojson-specification";
import type { Feature, FeatureCollection, Geometry, Position } from "geojson";
import { vec3ToLatLon, type LatLon } from "../../shared/geo";
import { DEG } from "./math";

export interface LandDots {
  /** xyz unit vectors, 3 floats per dot. */
  positions: Float32Array;
  /** per-dot attributes: [cityLight 0..1, random 0..1]. */
  attrs: Float32Array;
  count: number;
  /** Fibonacci sample count used (all dots, land + sea). */
  samples: number;
}

const MASK_W = 2048;
const MASK_H = 1024;

type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

function makeCanvas(): { ctx: Ctx2D } | null {
  if (typeof OffscreenCanvas !== "undefined") {
    const c = new OffscreenCanvas(MASK_W, MASK_H);
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (ctx) return { ctx };
  }
  if (typeof document !== "undefined") {
    const c = document.createElement("canvas");
    c.width = MASK_W;
    c.height = MASK_H;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (ctx) return { ctx };
  }
  return null;
}

function traceRing(ctx: Ctx2D, ring: Position[], shift: number): void {
  // Unwrap longitudes so rings touching the antimeridian stay contiguous.
  let prev: number | undefined;
  let off = 0;
  ring.forEach((pt, i) => {
    let lon = pt[0] ?? 0;
    const lat = pt[1] ?? 0;
    if (prev !== undefined) {
      const d = lon + off - prev;
      if (d > 180) off -= 360;
      else if (d < -180) off += 360;
    }
    lon += off;
    prev = lon;
    const x = ((lon + shift + 180) / 360) * MASK_W;
    const y = ((90 - lat) / 180) * MASK_H;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.closePath();
}

function ringLonRange(ring: Position[]): [number, number] {
  let prev: number | undefined;
  let off = 0;
  let lo = Infinity;
  let hi = -Infinity;
  for (const pt of ring) {
    let lon = pt[0] ?? 0;
    if (prev !== undefined) {
      const d = lon + off - prev;
      if (d > 180) off -= 360;
      else if (d < -180) off += 360;
    }
    lon += off;
    prev = lon;
    lo = Math.min(lo, lon);
    hi = Math.max(hi, lon);
  }
  return [lo, hi];
}

function drawPolygon(ctx: Ctx2D, rings: Position[][]): void {
  const outer = rings[0];
  if (!outer) return;
  const [lo, hi] = ringLonRange(outer);
  const shifts = [0];
  if (lo < -180) shifts.push(360);
  if (hi > 180) shifts.push(-360);
  for (const s of shifts) {
    ctx.beginPath();
    for (const r of rings) traceRing(ctx, r, s);
    ctx.fill("evenodd");
  }
}

function drawGeometry(ctx: Ctx2D, g: Geometry | null): void {
  if (!g) return;
  if (g.type === "Polygon") drawPolygon(ctx, g.coordinates);
  else if (g.type === "MultiPolygon") for (const p of g.coordinates) drawPolygon(ctx, p);
  else if (g.type === "GeometryCollection") for (const sub of g.geometries) drawGeometry(ctx, sub);
}

/** Rasterizes land into an equirectangular alpha mask (1 byte per pixel). */
export function rasterizeLand(topo: Topology): Uint8Array | null {
  const c = makeCanvas();
  if (!c) return null;
  const { ctx } = c;
  const obj = topo.objects["land"] as GeometryCollection | undefined;
  if (!obj) return null;
  const fc = feature(topo, obj) as FeatureCollection | Feature;
  ctx.fillStyle = "#fff";
  if (fc.type === "FeatureCollection") for (const f of fc.features) drawGeometry(ctx, f.geometry);
  else drawGeometry(ctx, fc.geometry);
  const img = ctx.getImageData(0, 0, MASK_W, MASK_H).data;
  const mask = new Uint8Array(MASK_W * MASK_H);
  for (let i = 0; i < mask.length; i++) mask[i] = img[i * 4 + 3]!;
  return mask;
}

function sampleMask(mask: Uint8Array, lat: number, lon: number): number {
  const x = Math.min(MASK_W - 1, Math.max(0, Math.floor(((lon + 180) / 360) * MASK_W)));
  const y = Math.min(MASK_H - 1, Math.max(0, Math.floor(((90 - lat) / 180) * MASK_H)));
  return mask[y * MASK_W + x]!;
}

/**
 * Generates land dots. `lights` are points (e.g. data-center cities) used to
 * derive a "city lights" attribute that glows on the night side.
 */
export function buildDotsFromMask(mask: Uint8Array, samples: number, lights: readonly LatLon[]): LandDots {
  const golden = Math.PI * (3 - Math.sqrt(5));
  const pos: number[] = [];
  const attrs: number[] = [];
  const lightVecs = lights.map((l) => {
    const p = l.lat * DEG;
    const la = l.lon * DEG;
    return [Math.cos(p) * Math.sin(la), Math.sin(p), Math.cos(p) * Math.cos(la)] as const;
  });
  const cosCut = Math.cos(6 * DEG);
  const sigma = 1.15 * DEG;
  let seed = 1234567;
  const rnd = () => {
    seed = (Math.imul(seed, 1103515245) + 12345) >>> 0;
    return (seed >>> 8) / 16777216;
  };
  for (let i = 0; i < samples; i++) {
    const y = 1 - (2 * (i + 0.5)) / samples;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const th = golden * i;
    const x = Math.sin(th) * r;
    const z = Math.cos(th) * r;
    const ll = vec3ToLatLon([x, y, z]);
    if (sampleMask(mask, ll.lat, ll.lon) < 128) continue;
    let city = 0;
    for (const l of lightVecs) {
      const d = x * l[0] + y * l[1] + z * l[2];
      if (d < cosCut) continue;
      const ang = Math.acos(Math.min(1, d));
      city += Math.exp(-(ang * ang) / (sigma * sigma));
    }
    pos.push(x, y, z);
    attrs.push(Math.min(1, city * 0.85), rnd());
  }
  return { positions: new Float32Array(pos), attrs: new Float32Array(attrs), count: pos.length / 3, samples };
}

export async function loadLandDots(url: string, samples: number, lights: readonly LatLon[]): Promise<LandDots> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`globe: failed to load ${url}: ${res.status}`);
  const topo = (await res.json()) as Topology;
  const mask = rasterizeLand(topo);
  if (!mask) throw new Error("globe: 2D canvas unavailable for land rasterization");
  return buildDotsFromMask(mask, samples, lights);
}
